import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { ExternalLink, Search } from 'lucide-react';
import { detailNumber, detailText, fetchProducts } from '../api/products';
import type { Product, ProductFilters } from '../api/products';
import ExportButton from '../components/ExportButton';
import { HeaderActions } from '../components/HeaderActions';
import { LabelBadge } from '../components/badges';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH_COMPACT,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  NsIdLink,
  expandColumn,
} from '../components/data-table/columns';
import { RowActionLink, RowActionMenu, RowActions, type RowMenuItem } from '../components/data-table/RowActions';
import {
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtPrice } from '../utils/format';
import { useItemDetail } from '../hooks/useItemDetail';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const STATUS_OPTIONS = [
  { value: 'active',   label: 'Active',   dot: 'bg-green-500' },
  { value: 'inactive', label: 'Inactive', dot: 'bg-zinc-400'  },
  { value: 'all',      label: 'All' },
];

export default function ProductsPage() {
  const [filters, setFilters] = useState<ProductFilters>({ page: 1, per_page: 50, status: 'active' });
  // The catalogue is large, so text filters apply on submit rather than on every
  // keystroke — one request per search instead of one per character.
  const [draftSearch, setDraftSearch]             = useState('');
  const [draftVendor, setDraftVendor]             = useState('');
  const [draftManufacturer, setDraftManufacturer] = useState('');
  const [draftPriceMin, setDraftPriceMin]         = useState('');
  const [draftPriceMax, setDraftPriceMax]         = useState('');
  const [draftOnHandMin, setDraftOnHandMin]       = useState('');
  const [draftStatus, setDraftStatus]             = useState<'active' | 'inactive' | 'all'>('active');

  const [items, setItems]     = useState<Product[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const { detailFor, loadDetail } = useItemDetail();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchProducts(filters);
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void load(); }, [load]);

  function applyFilters(e: React.FormEvent) {
    e.preventDefault();
    const priceMin  = parseFloat(draftPriceMin);
    const priceMax  = parseFloat(draftPriceMax);
    const onHandMin = parseInt(draftOnHandMin, 10);
    setFilters(f => ({
      search:             draftSearch.trim() || undefined,
      vendor:             draftVendor.trim() || undefined,
      manufacturer:       draftManufacturer.trim() || undefined,
      purchase_price_min: isNaN(priceMin)  ? undefined : priceMin,
      purchase_price_max: isNaN(priceMax)  ? undefined : priceMax,
      on_hand_min:        isNaN(onHandMin) ? undefined : onHandMin,
      status:             draftStatus,
      page: 1,
      per_page: f.per_page ?? 50,
    }));
  }

  function resetFilters() {
    setDraftSearch('');
    setDraftVendor('');
    setDraftManufacturer('');
    setDraftPriceMin('');
    setDraftPriceMax('');
    setDraftOnHandMin('');
    setDraftStatus('active');
    setFilters(f => ({ page: 1, per_page: f.per_page ?? 50, status: 'active' }));
  }

  const hasActiveFilters =
    !!draftSearch || !!draftVendor || !!draftManufacturer ||
    !!draftPriceMin || !!draftPriceMax || !!draftOnHandMin || draftStatus !== 'active';

  const columns = useMemo<ColumnDef<Product>[]>(() => [
    expandColumn<Product>(),
    {
      // SKU leads here as it does everywhere else — this view used to start
      // with the NS ID, which broke the scan pattern between tables (BOT-392).
      id: 'sku',
      size: 230,
      enableHiding: false,
      meta: { label: 'SKU' },
      header: 'SKU',
      cell: ({ row }) => (
        <>
          <div className="whitespace-nowrap font-mono text-[12.5px] font-medium">{row.original.sku ?? '—'}</div>
          <div className="max-w-[240px] truncate text-xs text-muted-foreground" title={row.original.name ?? ''}>
            {row.original.name ?? ''}
          </div>
        </>
      ),
    },
    {
      id: 'ns',
      meta: { label: 'NS ID' },
      header: 'NS ID',
      cell: ({ row }) => <NsIdLink id={row.original.ns_product_id} />,
    },
    {
      id: 'vendor',
      meta: { label: 'Vendor' },
      header: 'Vendor',
      cell: ({ row }) =>
        row.original.vendor ? (
          <Badge variant="neutral" className="font-mono">{row.original.vendor}</Badge>
        ) : (
          <span className="text-zinc-300">—</span>
        ),
    },
    {
      id: 'manufacturer',
      meta: { label: 'Manufacturer' },
      header: 'Manufacturer',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-zinc-700">{row.original.manufacturer ?? '—'}</span>
      ),
    },
    {
      id: 'purchase',
      meta: { label: 'Purchase cost', align: 'right' },
      header: 'Purchase cost',
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          <div className="font-medium">{fmtPrice(row.original.purchase_price)}</div>
          {row.original.core_charge != null && (
            <div className="text-[11.5px] text-zinc-400">{fmtPrice(row.original.core_charge)} core</div>
          )}
        </div>
      ),
    },
    {
      id: 'website',
      meta: { label: 'Website price', align: 'right' },
      header: 'Website price',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">{fmtPrice(row.original.website_price)}</span>
      ),
    },
    {
      id: 'onHand',
      meta: { label: 'On hand', align: 'right' },
      header: 'On hand',
      cell: ({ row }) => (
        <span className={row.original.on_hand > 0 ? 'text-foreground' : 'text-zinc-400'}>
          {row.original.on_hand}
        </span>
      ),
    },
    {
      id: 'status',
      meta: { label: 'Status' },
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1">
          {row.original.inactive && <Badge variant="neutral" dot="bg-zinc-400">Inactive</Badge>}
          {row.original.item_status ? (
            <LabelBadge value={row.original.item_status} />
          ) : (
            !row.original.inactive && <span className="text-zinc-300">—</span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      // Two controls, not three — the compact width, per the review.
      size: ACTIONS_COL_WIDTH_COMPACT,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const p = row.original;
        // Details is the action this table exists for, so it is a button rather
        // than a menu entry — one click instead of two.
        const menuItems: RowMenuItem[] = [
          { label: 'Open in NetSuite', icon: ExternalLink, href: `${NS_BASE_URL}${p.ns_product_id}` },
        ];
        if (p.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento',
            icon: ExternalLink,
            href: `${MAGENTO_BASE_URL}${p.magento_id}`,
          });
        }
        return (
          <RowActions>
            <RowActionLink label="Details" icon={Search} href={`/all-items/${p.ns_product_id}?from=all`} />
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    },
  ], []);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.ns_product_id),
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { columnPinning: { left: ['expand', 'sku'], right: ['actions'] } },
  });

  useEffect(() => {
    if (expanded === true) return;
    Object.keys(expanded).filter(id => expanded[id]).forEach(id => void loadDetail(id));
  }, [expanded, loadDetail]);

  const perPage     = filters.per_page ?? 50;
  const currentPage = filters.page ?? 1;
  const totalPages  = Math.max(1, Math.ceil(total / perPage));

  return (
    <>
      <HeaderActions>
        <ExportButton resource="all_items" filters={{ ...filters, page: undefined, per_page: undefined }} />
      </HeaderActions>

      <PageHeading title="All Items" count={total} />

      <form onSubmit={applyFilters}>
        <FilterBar>
          <FilterRow className="gap-3">
            <FilterField label="SKU / name">
              <Input
                type="text"
                placeholder="Search…"
                className="w-56"
                value={draftSearch}
                onChange={e => setDraftSearch(e.target.value)}
              />
            </FilterField>
            <FilterField label="Vendor">
              <Input
                type="text"
                placeholder="e.g. pacc"
                className="w-28 font-mono"
                value={draftVendor}
                onChange={e => setDraftVendor(e.target.value)}
              />
            </FilterField>
            <FilterField label="Manufacturer">
              <Input
                type="text"
                placeholder="e.g. CAT"
                className="w-32"
                value={draftManufacturer}
                onChange={e => setDraftManufacturer(e.target.value)}
              />
            </FilterField>
            <FilterSelect
              label="Status"
              options={STATUS_OPTIONS}
              value={draftStatus}
              onChange={v => setDraftStatus(v as 'active' | 'inactive' | 'all')}
            />
          </FilterRow>

          <FilterRow className="gap-4">
            <FilterField label="Purchase $">
              <Input
                type="number"
                placeholder="min"
                min={0}
                step={0.01}
                className="w-[68px]"
                value={draftPriceMin}
                onChange={e => setDraftPriceMin(e.target.value)}
              />
              <span className="text-zinc-300">–</span>
              <Input
                type="number"
                placeholder="max"
                min={0}
                step={0.01}
                className="w-[68px]"
                value={draftPriceMax}
                onChange={e => setDraftPriceMax(e.target.value)}
              />
            </FilterField>

            <FilterField label="On hand ≥">
              <Input
                type="number"
                placeholder="0"
                min={0}
                step={1}
                className="w-[68px]"
                value={draftOnHandMin}
                onChange={e => setDraftOnHandMin(e.target.value)}
              />
            </FilterField>

            <Button type="submit">
              <Search className="h-3.5 w-3.5" />
              Search
            </Button>

            <div className="ml-auto flex items-center gap-2">
              {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
              <ColumnsMenu table={table} />
            </div>
          </FilterRow>
        </FilterBar>
      </form>

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="No products found"
        renderExpanded={row => {
          const detail = detailFor(row.original.ns_product_id);
          if (detail.loading) {
            return <div className="py-4 pl-[84px] text-[12.5px] text-muted-foreground">Loading details…</div>;
          }
          if (detail.error) {
            return <div className="py-4 pl-[84px] text-[12.5px] text-red-600">{detail.error}</div>;
          }
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    ['Display name', detailText(detail.data, 'display_name') ?? '—'],
                    ['Old part number', detailText(detail.data, 'old_part_number') ?? '—'],
                    ['Type', detailText(detail.data, 'product_type') ?? '—'],
                    ['Country', detailText(detail.data, 'manufacturer_country') ?? '—'],
                    ['Warranty', detailText(detail.data, 'warranty') ?? '—'],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Pricing & stock">
                <ExpandedFields
                  rows={[
                    ['Special price', fmtPrice(detailNumber(detail.data, 'special_price'))],
                    ['Core charge', fmtPrice(row.original.core_charge)],
                    ['Website visible', detailText(detail.data, 'website_visible') ?? '—'],
                    ['Returnable', detailText(detail.data, 'returnable_status') ?? '—'],
                    ['Lead time', detailText(detail.data, 'home_value') ?? '—'],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Dimensions">
                <ExpandedFields
                  rows={[
                    ['Weight', detailText(detail.data, 'weight') ?? '—'],
                    ['Height', detailText(detail.data, 'height') ?? '—'],
                    ['Width', detailText(detail.data, 'width') ?? '—'],
                    ['Length', detailText(detail.data, 'length') ?? '—'],
                  ]}
                />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={currentPage}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        onPerPageChange={n => setFilters(f => ({ ...f, per_page: n, page: 1 }))}
      />
    </>
  );
}
